import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  useLoadClaudeAccessStatusQuery,
  useConfirmClaudeAccessMutation,
  useUploadClaudeCertMutation,
} from '../app/api';
import { useIsMobile } from './utils';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
};

function StepCard({ number, title, children, done }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 12px rgba(10,41,71,0.08)',
      overflow: 'hidden',
      marginBottom: '1.25rem',
      border: done ? `2px solid ${brand.green}` : `2px solid transparent`,
    }}>
      <div style={{
        background: brand.deepBlue,
        padding: '0.875rem 1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        borderBottom: `3px solid ${done ? brand.green : brand.goldenrod}`,
      }}>
        <span style={{
          background: done ? brand.green : brand.goldenrod,
          color: 'white',
          borderRadius: '50%',
          width: '1.75rem',
          height: '1.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: '0.85rem',
          flexShrink: 0,
        }}>
          {done ? <i className="bi bi-check" /> : number}
        </span>
        <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
          {title}
        </h5>
        {done && (
          <span style={{ marginLeft: 'auto', color: brand.green, background: 'rgba(26,127,78,0.15)', borderRadius: '20px', padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700 }}>
            Complete
          </span>
        )}
      </div>
      <div style={{ padding: '1.25rem' }}>
        {children}
      </div>
    </div>
  );
}

StepCard.propTypes = {
  number: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  done: PropTypes.bool.isRequired,
};

export default function ClaudeAccess() {
  const isMobile = useIsMobile();
  const { data: status, isLoading, refetch } = useLoadClaudeAccessStatusQuery();
  const [triggerConfirm] = useConfirmClaudeAccessMutation();
  const [triggerUpload] = useUploadClaudeCertMutation();

  const [policies, setPolicies] = useState(false);
  const [training, setTraining] = useState(false);
  const [certFile, setCertFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Seed local state from loaded record.
  useEffect(() => {
    if (status) {
      setPolicies(!!status.policies_confirmed);
      setTraining(!!status.training_confirmed);
    }
  }, [status]);

  const hasCert = !!(status?.certificate_s3_key);
  const isSubmitted = !!(status?.submitted_at);
  const allDone = policies && training && (hasCert || certFile);

  const sourceEmail = status?.admin_username || status?.user_email || '';
  const localPart = sourceEmail.includes('@') ? sourceEmail.split('@')[0] : sourceEmail;
  const claudeSignInEmail = localPart ? `${localPart}@prosperitycapitaladvisors.com` : '';

  const handleCheckboxChange = async (field, value) => {
    if (field === 'policies') setPolicies(value);
    else setTraining(value);

    const next = {
      policies_confirmed: field === 'policies' ? value : policies,
      training_confirmed: field === 'training' ? value : training,
    };
    setSaving(true);
    try {
      await triggerConfirm(next).unwrap();
      await refetch();
    } catch (_e) {
      // save errors are non-critical; checkboxes remain toggled in local state
    }
    setSaving(false);
  };

  const handleUpload = async () => {
    if (!certFile) return;
    setUploadError(null);
    setUploading(true);

    const formData = new FormData();
    formData.append('file', certFile);

    try {
      await triggerUpload({ body: formData }).unwrap();
      setCertFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await refetch();
    } catch (err) {
      setUploadError(err?.data?.detail || 'Upload failed. Please try again.');
    }
    setUploading(false);
  };

  if (isLoading) {
    return (
      <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner-border" style={{ color: brand.tealBlue }} />
      </div>
    );
  }

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>

      {/* Header banner */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: isMobile ? '1.25rem' : '1.75rem 2rem', marginBottom: isMobile ? '1rem' : '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '300px', height: '100%', background: 'linear-gradient(135deg, transparent 40%, rgba(0,105,166,0.3))', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.75rem', flexShrink: 0 }}>
            <i className="bi bi-robot" style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              Enterprise AI Initiative
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px', fontSize: isMobile ? '1.25rem' : undefined }}>Access to Claude</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: '0.875rem' }}>
              Complete the steps below to activate your Claude Enterprise account.
            </p>
          </div>
          {isSubmitted && (
            <div style={{ marginLeft: isMobile ? 0 : 'auto', background: brand.green, borderRadius: '12px', padding: '0.75rem 1.25rem', textAlign: 'center', flexShrink: 0 }}>
              <i className="bi bi-patch-check-fill" style={{ color: 'white', fontSize: '1.5rem', display: 'block' }} />
              <div style={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', marginTop: '4px' }}>Submitted</div>
            </div>
          )}
        </div>
      </div>

      {isSubmitted && (
        <div style={{
          background: brand.green,
          borderRadius: '12px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
          color: 'white',
          boxShadow: '0 2px 12px rgba(26,127,78,0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.875rem',
        }}>
          <i className="bi bi-check-circle-fill" style={{ fontSize: '1.75rem', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '2px' }}>
              Submission Complete!
            </div>
            <div style={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
              You will receive an email when your account is activated.
              {claudeSignInEmail && (
                <> Your account sign in to Claude will be <strong>{claudeSignInEmail}</strong>.</>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="row g-4">
        <div className="col-lg-8">

          {/* Intro */}
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: '1.25rem', marginBottom: '1.25rem' }}>
            <h5 style={{ color: brand.deepBlue, fontWeight: 700, marginBottom: '0.75rem' }}>Welcome to Claude 101</h5>
            <p style={{ color: brand.charcoal, fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '0.5rem' }}>
              We&apos;re taking a structured approach to AI across the firm — focusing on real use cases, practical value, and compliance-first adoption. Whether you&apos;re new to AI or already using tools like ChatGPT, this process will help you build confidence and apply Claude in a meaningful way.
            </p>
            <p style={{ color: brand.charcoal, fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>
              Complete the three steps below. Once all steps are verified, your Claude Enterprise account will be activated.
            </p>
          </div>

          {/* Step 1 — Policies */}
          <StepCard number="1" title="Read Company AI Policies" done={policies}>
            <p style={{ color: brand.charcoal, fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>
              Review the firm&apos;s AI usage policies and acceptable use guidelines before proceeding. These policies cover data handling, client confidentiality, and responsible use of AI tools in your day-to-day work.
            </p>
            <a
              href="https://c2p.box.com/s/qqd5vfeegz5l7jqto5y2hitoxoy2amp3"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: brand.tealBlue, color: 'white', padding: '0.5rem 1rem', borderRadius: '8px', textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem', marginBottom: '1rem' }}
            >
              <i className="bi bi-box-arrow-up-right" />
              AI Policies Document
            </a>
            <div className="form-check" style={{ padding: '0.875rem 1rem 0.875rem 2.5rem', background: policies ? 'rgba(26,127,78,0.06)' : 'rgba(10,41,71,0.04)', borderRadius: '8px', border: `1px solid ${policies ? 'rgba(26,127,78,0.2)' : 'rgba(10,41,71,0.1)'}` }}>
              <input
                className="form-check-input"
                type="checkbox"
                id="policiesCheck"
                checked={policies}
                onChange={e => handleCheckboxChange('policies', e.target.checked)}
                style={{ borderColor: brand.tealBlue, cursor: 'pointer' }}
              />
              <label className="form-check-label" htmlFor="policiesCheck" style={{ fontWeight: 600, color: brand.deepBlue, cursor: 'pointer', fontSize: '0.9rem', userSelect: 'none' }}>
                I have read and agree to the company AI usage policies
              </label>
            </div>
          </StepCard>

          {/* Step 2 — Training */}
          <StepCard number="2" title="Complete Claude 101 Training" done={training}>
            <p style={{ color: brand.charcoal, fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '0.75rem' }}>
              Take the Claude 101 course on Anthropic&apos;s learning platform. This course covers Claude&apos;s core features and best practices, and provides additional resources for using AI effectively. Once finished, you&apos;ll receive a certificate to upload in Step 3.
            </p>
            <p style={{ color: brand.charcoal, fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '0.75rem' }}>
              <b>Note:</b>This link will take you to Skilljar, Anthropic&apos;s training platform. 
            </p>
            <p style={{ color: brand.charcoal, fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '0.75rem' }}>
              Once the page opens, click on the &quot;Claude 101&quot; course and sign up to begin.
            </p>
            <a
              href="https://anthropic.skilljar.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: brand.tealBlue, color: 'white', padding: '0.5rem 1rem', borderRadius: '8px', textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem', marginBottom: '1rem' }}
            >
              <i className="bi bi-box-arrow-up-right" />
              Open Anthropic Training Courses
            </a>
            <div className="form-check" style={{ padding: '0.875rem 1rem 0.875rem 2.5rem', background: training ? 'rgba(26,127,78,0.06)' : 'rgba(10,41,71,0.04)', borderRadius: '8px', border: `1px solid ${training ? 'rgba(26,127,78,0.2)' : 'rgba(10,41,71,0.1)'}` }}>
              <input
                className="form-check-input"
                type="checkbox"
                id="trainingCheck"
                checked={training}
                onChange={e => handleCheckboxChange('training', e.target.checked)}
                style={{ borderColor: brand.tealBlue, cursor: 'pointer' }}
              />
              <label className="form-check-label" htmlFor="trainingCheck" style={{ fontWeight: 600, color: brand.deepBlue, cursor: 'pointer', fontSize: '0.9rem', userSelect: 'none' }}>
                I have completed the Claude 101 training course
              </label>
            </div>
          </StepCard>

          {/* Step 3 — Certificate Upload */}
          <StepCard number="3" title="Upload Your Completion Certificate" done={hasCert}>
            <p style={{ color: brand.charcoal, fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>
              Upload the certificate you received after completing the Claude 101 course. Accepted formats: PDF, JPG, PNG (max 10 MB).
            </p>

            {hasCert && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(26,127,78,0.06)', border: '1px solid rgba(26,127,78,0.2)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
                <i className="bi bi-file-earmark-check-fill" style={{ color: brand.green, fontSize: '1.1rem' }} />
                <div>
                  <div style={{ fontWeight: 700, color: brand.green, fontSize: '0.85rem' }}>Certificate on file</div>
                  {status?.certificate_filename && (
                    <div style={{ color: brand.charcoal, fontSize: '0.8rem' }}>{status.certificate_filename}</div>
                  )}
                </div>
                <button
                  onClick={() => { setCertFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; fileInputRef.current?.click(); }}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: brand.tealBlue, fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Replace
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="form-control"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => { setCertFile(e.target.files[0] || null); setUploadError(null); }}
                  style={{ fontSize: '0.875rem' }}
                />
              </div>
              <button
                onClick={handleUpload}
                disabled={!certFile || uploading}
                style={{
                  background: certFile && !uploading ? brand.tealBlue : brand.charcoal,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.5rem 1.25rem',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  cursor: certFile && !uploading ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  opacity: !certFile || uploading ? 0.65 : 1,
                }}
              >
                {uploading
                  ? <><span className="spinner-border spinner-border-sm" /> Uploading…</>
                  : <><i className="bi bi-cloud-upload" /> Upload</>
                }
              </button>
            </div>

            {uploadError && (
              <div style={{ marginTop: '0.75rem', color: '#c0392b', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <i className="bi bi-exclamation-circle" /> {uploadError}
              </div>
            )}
          </StepCard>

        </div>

        {/* Sidebar — progress summary */}
        <div className="col-lg-4">
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden', position: isMobile ? 'static' : 'sticky', top: '1.5rem' }}>
            <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <i className="bi bi-list-check" style={{ color: brand.goldenrod }} />
              <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Your Progress</h5>
            </div>
            <div style={{ padding: '1.25rem' }}>
              {[
                { label: 'Read AI Policies',      done: policies },
                { label: 'Completed Claude 101',  done: training },
                { label: 'Certificate Uploaded',  done: hasCert },
              ].map(({ label, done }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.875rem' }}>
                  <span style={{
                    width: '1.4rem',
                    height: '1.4rem',
                    borderRadius: '50%',
                    background: done ? brand.green : brand.softGray,
                    border: done ? 'none' : `2px solid ${brand.charcoal}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {done && <i className="bi bi-check" style={{ color: 'white', fontSize: '0.8rem', fontWeight: 800 }} />}
                  </span>
                  <span style={{ color: done ? brand.deepBlue : brand.charcoal, fontWeight: done ? 700 : 400, fontSize: '0.875rem' }}>{label}</span>
                </div>
              ))}

              <div style={{ borderTop: `1px solid ${brand.softGray}`, marginTop: '0.5rem', paddingTop: '1rem' }}>
                {isSubmitted ? (
                  <div style={{ textAlign: 'center' }}>
                    <i className="bi bi-patch-check-fill" style={{ color: brand.green, fontSize: '2rem' }} />
                    <p style={{ color: brand.green, fontWeight: 700, margin: '0.5rem 0 0.25rem' }}>Submission Complete</p>
                    <p style={{ color: brand.charcoal, fontSize: '0.8rem', margin: 0 }}>
                      Your request has been submitted. You&apos;ll receive an email when your account is activated.
                    </p>
                  </div>
                ) : allDone ? (
                  <div style={{ textAlign: 'center' }}>
                    <i className="bi bi-hourglass-split" style={{ color: brand.goldenrod, fontSize: '1.5rem' }} />
                    <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
                      All steps complete — waiting for admin review.
                    </p>
                  </div>
                ) : (
                  <p style={{ color: brand.charcoal, fontSize: '0.8rem', margin: 0, lineHeight: 1.5 }}>
                    Complete all three steps to submit your request for account activation.
                  </p>
                )}
              </div>

              {saving && (
                <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem', color: brand.charcoal, fontSize: '0.8rem' }}>
                  <span className="spinner-border spinner-border-sm" style={{ width: '0.75rem', height: '0.75rem' }} /> Saving…
                </div>
              )}

              {status?.submitted_at && (
                <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: brand.charcoal }}>
                  Submitted: {new Date(status.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
